import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { libraryOriginBack, withLibraryContext } from '@/lib/library-navigation';
import { h5pRelatedCourseHref } from '@/lib/course-admin-navigation';
import {
  packageLibraryLabel,
  packageStatusLabel,
  packageVersionCount,
} from './h5p-package-utils';

type DetailTab = 'info' | 'dependencies' | 'versions' | 'courses';

interface H5pLibraryRef {
  id: string;
  name: string;
  machineName: string;
  major: number;
  minor: number;
  patch: number;
}

interface H5pDependency {
  id: string;
  dependencyType: string;
  requiredLibrary: H5pLibraryRef;
}

interface H5pPackageDetail {
  id: string;
  title: string;
  slug: string;
  status?: string;
  description?: string;
  license?: string;
  language?: string;
  versionCount?: number;
  courseCount?: number;
  creatorName?: string | null;
  authorInitials?: string;
  tags?: string[];
  mainLibrary?: H5pLibraryRef | null;
  versions?: { id: string; versionNumber: number; createdAt: string }[];
  dependencies?: H5pDependency[];
  courses?: { id: string; name: string; code?: string | null }[];
}

function libraryVersionString(lib?: H5pLibraryRef | null): string {
  if (!lib) return '—';
  return `${lib.machineName} ${lib.major}.${lib.minor}.${lib.patch}`;
}

export function H5pShowPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.H5P_EDITAR);
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<DetailTab>('info');
  const back = libraryOriginBack(searchParams, '/h5p', 'Volver a Biblioteca H5P');

  const { data, isLoading, error } = useQuery({
    queryKey: ['h5p', id],
    queryFn: () => api<H5pPackageDetail>(`/h5p/packages/${id}`),
    enabled: Boolean(id),
  });

  const tabs: { id: DetailTab; label: string; count?: number }[] = [
    { id: 'info', label: 'Información' },
    { id: 'dependencies', label: 'Dependencias' },
    { id: 'versions', label: 'Versiones' },
    { id: 'courses', label: 'Cursos relacionados', count: data?.courseCount ?? 0 },
  ];

  return (
    <div className="lms-h5p-library lms-h5p-detail lms-site-admin-hub-page font-sans">
      <header className="lms-h5p-library__header">
        <div className="flex items-start gap-4">
          <HubBackLink to={back.to} label={back.label} />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
              {data?.title ?? 'Actividad H5P'}
            </h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              {data ? packageLibraryLabel(data) : 'Actividad H5P reutilizable'}
            </p>
          </div>
        </div>
        {id && (
          <div className="lms-h5p-library__header-actions">
            <Link to={withLibraryContext(`/h5p/${id}/preview`, searchParams)} className="lms-h5p-library__btn lms-h5p-library__btn--outline">
              Vista previa
            </Link>
            {canEdit && (
              <Link to={withLibraryContext(`/h5p/${id}/edit`, searchParams)} className="lms-h5p-library__btn lms-h5p-library__btn--primary">
                <span className="material-symbols-outlined" aria-hidden="true">
                  edit
                </span>
                Editar
              </Link>
            )}
          </div>
        )}
      </header>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : data ? (
        <>
          <div className="lms-h5p-detail__tabs">
            <nav className="lms-h5p-detail__tabs-nav" aria-label="Secciones de la actividad">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`lms-h5p-detail__tab${activeTab === tab.id ? ' lms-h5p-detail__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  {tab.count != null ? <span className="lms-h5p-detail__tab-count">{tab.count}</span> : null}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'info' && (
            <div className="lms-h5p-detail__grid">
              <div className="lms-h5p-detail__main">
                <section className="lms-h5p-detail__card">
                  <h2 className="lms-h5p-detail__card-title">Descripción</h2>
                  <p className="lms-h5p-detail__description">
                    {data.description?.trim() || 'Sin descripción.'}
                  </p>
                </section>

                <section className="lms-h5p-detail__preview">
                  <div className="lms-h5p-detail__preview-panel">
                    <span className="material-symbols-outlined" aria-hidden="true">
                      drag_indicator
                    </span>
                    <h3 className="lms-h5p-detail__card-title">Vista Previa Visual</h3>
                    {id ? (
                      <Link to={withLibraryContext(`/h5p/${id}/preview`, searchParams)} className="lms-h5p-detail__preview-btn">
                        Iniciar Vista Previa
                      </Link>
                    ) : null}
                  </div>
                </section>
              </div>

              <aside className="lms-h5p-detail__aside">
                <section className="lms-h5p-detail__card">
                  <h2 className="lms-h5p-detail__card-title">
                    <span className="material-symbols-outlined align-middle" aria-hidden="true">
                      info
                    </span>{' '}
                    Detalles Técnicos
                  </h2>
                  <dl className="lms-h5p-detail__meta-list">
                    <div>
                      <dt>Estado</dt>
                      <dd>
                        <span className="lms-h5p-detail__badge lms-h5p-detail__badge--active">
                          {packageStatusLabel(data.status)}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt>Librería</dt>
                      <dd className="lms-h5p-detail__meta-value">
                        <span className="material-symbols-outlined" aria-hidden="true">
                          extension
                        </span>
                        {packageLibraryLabel(data)}
                      </dd>
                    </div>
                    <div className="lms-h5p-detail__meta-grid">
                      <div>
                        <dt>Versión</dt>
                        <dd>v{packageVersionCount(data)}</dd>
                      </div>
                      <div>
                        <dt>Cursos</dt>
                        <dd className="lms-h5p-detail__meta-value">
                          <span className="material-symbols-outlined" aria-hidden="true">
                            school
                          </span>
                          {data.courseCount ?? 0} cursos
                        </dd>
                      </div>
                    </div>
                    <div>
                      <dt>Autor</dt>
                      <dd className="lms-h5p-detail__author">
                        <span className="lms-h5p-detail__author-avatar">{data.authorInitials ?? 'SA'}</span>
                        {data.creatorName ?? 'Super Administrador'}
                      </dd>
                    </div>
                    <div className="lms-h5p-detail__meta-grid">
                      <div>
                        <dt>Licencia</dt>
                        <dd>
                          <span className="material-symbols-outlined align-middle text-[18px]" aria-hidden="true">
                            copyright
                          </span>{' '}
                          {data.license ?? 'U'}
                        </dd>
                      </div>
                      <div>
                        <dt>Idioma</dt>
                        <dd>
                          <span className="material-symbols-outlined align-middle text-[18px]" aria-hidden="true">
                            language
                          </span>{' '}
                          {data.language?.toUpperCase() ?? 'ES'}
                        </dd>
                      </div>
                    </div>
                    <div>
                      <dt>Categoría</dt>
                      <dd className="italic text-[var(--hd-on-surface-variant)]">— Sin asignar —</dd>
                    </div>
                    <div>
                      <dt>Etiquetas</dt>
                      <dd className="lms-h5p-detail__tags">
                        {(data.tags ?? []).map((tag) => (
                          <span
                            key={tag}
                            className={`lms-h5p-detail__tag${tag === 'seguridad' ? ' lms-h5p-detail__tag--accent' : ''}`}
                          >
                            {tag}
                          </span>
                        ))}
                      </dd>
                    </div>
                    <div>
                      <dt>UUID</dt>
                      <dd className="lms-h5p-detail__uuid">{data.id}</dd>
                    </div>
                  </dl>
                </section>
              </aside>
            </div>
          )}

          {activeTab === 'dependencies' && (
            <section className="lms-h5p-detail__card">
              <h2 className="lms-h5p-detail__panel-title">Librería principal</h2>
              {data.mainLibrary ? (
                <div className="lms-h5p-detail__list-item mb-6">
                  <div>
                    <p className="font-medium">{libraryVersionString(data.mainLibrary)}</p>
                    <p className="text-xs text-[var(--hd-on-surface-variant)]">
                      {data.mainLibrary.name}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="lms-h5p-detail__empty">Sin librería principal registrada.</p>
              )}

              <h3 className="lms-h5p-detail__panel-title">Dependencias requeridas</h3>
              {(data.dependencies ?? []).length > 0 ? (
                <div className="lms-h5p-detail__list">
                  {data.dependencies!.map((dep) => (
                    <div key={dep.id} className="lms-h5p-detail__list-item">
                      <span>{libraryVersionString(dep.requiredLibrary)}</span>
                      <span className="lms-h5p-detail__tag">{dep.dependencyType}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="lms-h5p-detail__empty">Esta librería no declara dependencias.</p>
              )}
            </section>
          )}

          {activeTab === 'versions' && (
            <section className="lms-h5p-detail__card">
              <h2 className="lms-h5p-detail__panel-title">Historial de versiones</h2>
              {(data.versions ?? []).length > 0 ? (
                <div className="lms-h5p-detail__list">
                  {data.versions!.map((version) => (
                    <div key={version.id} className="lms-h5p-detail__list-item">
                      <span>Versión {version.versionNumber}</span>
                      <span className="text-[var(--hd-on-surface-variant)]">
                        {new Date(version.createdAt).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="lms-h5p-detail__empty">No hay versiones registradas.</p>
              )}
            </section>
          )}

          {activeTab === 'courses' && (
            <section className="lms-h5p-detail__card">
              <h2 className="lms-h5p-detail__panel-title">Cursos relacionados</h2>
              {(data.courses ?? []).length > 0 ? (
                <div className="lms-h5p-detail__list">
                  {data.courses!.map((course) => (
                    <div key={course.id} className="lms-h5p-detail__list-item">
                      <Link
                        to={id ? h5pRelatedCourseHref(course.id, id, searchParams) : `/courses/${course.id}`}
                        className="lms-h5p-detail__course-link"
                      >
                        {course.name}
                      </Link>
                      {course.code ? (
                        <span className="text-[var(--hd-on-surface-variant)]">{course.code}</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="lms-h5p-detail__empty">Esta actividad no está vinculada a ningún curso.</p>
              )}
            </section>
          )}
        </>
      ) : null}
    </div>
  );
}
